import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { ResolveIeltsMockTest, ResolvePteMockTest } from '../../Client/request';
import { Base_URL } from '../../Client/apiURL';
import { getSharedRouteAction, parseTestKey, storeLaunchGrant } from '../../utils/mockTestLinks';
import MockTestFull from '../../components/MockTest/MockTestFull';
import MockTestSectional from '../../components/MockTest/MockTestSectional';
import IELTSFullTestPage from '../IELTSFullTestPage';
import ListeningTestPage from '../ListeningTestPage';
import ReadingTestPage from '../ReadingTestPage';
import WritingTestPage from '../WritingTestPage';
import SpeakingTestPage from '../SpeakingTestPage';
import useMockTestAccess from '../../hooks/useMockTestAccess';
import MockTestAccessStatus from '../../components/Common/MockTestAccessStatus';
import { canAccessMockTest, fetchMockTestAccess } from '../../utils/mockTestAccess';
import { IeltsAttemptContext, startIeltsSection } from '../../components/IELTS/utils/ieltsStart';
import { v4 as uuid } from 'uuid';
import PteAttemptContext from '../../components/MockTest/PteAttemptContext';

const RunnerByCategory = {
  Listening: ListeningTestPage,
  Reading: ReadingTestPage,
  Writing: WritingTestPage,
  Speaking: SpeakingTestPage,
};

function responseError(data, fallback) {
  const error = new Error(data?.message || fallback);
  error.status = data?.responseCode;
  error.errorCode = data?.errorCode;
  return error;
}

export default function SharedMockTestRoute({ exam }) {
  const { scope, testKey } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const mockAccess = useMockTestAccess();
  const [errorCode, setErrorCode] = useState(null);
  const pendingDraftKey = useRef(null);
  const [launchChecked, setLaunchChecked] = useState(false);
  const parsed = useMemo(() => parseTestKey(testKey), [testKey]);
  const [resolved, setResolved] = useState(location.state?.resolvedTest || null);
  const [loading, setLoading] = useState(Boolean(parsed) && !location.state?.resolvedTest);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState(parsed ? null : 'This mock-test link is invalid.');
  const autoLaunchStarted = useRef(false);
  const resolvedMatchesRoute = Boolean(
    resolved
    && parsed
    && resolved.exam === exam
    && resolved.scope === scope
    && Number(resolved.id) === Number(parsed.id)
  );

  useEffect(() => {
    autoLaunchStarted.current = false;
    pendingDraftKey.current = null;
    setLaunchChecked(false);
  }, [exam, parsed?.id, scope]);

  const loadMetadata = useCallback(async () => {
    if (!parsed) return;
    setLoading(true);
    setError(null);
    setErrorCode(null);
    try {
      const data = exam === 'pte'
        ? await ResolvePteMockTest(scope, parsed.id)
        : await ResolveIeltsMockTest(scope, parsed.id);
      if (data?.responseCode !== 200 || !data?.response) throw responseError(data, 'Unable to load this mock test.');
      setResolved(data.response);
      if (data.response.canonicalPath && data.response.canonicalPath !== location.pathname) {
        navigate(data.response.canonicalPath, { replace: true, state: location.state });
      }
    } catch (requestError) {
      setErrorCode(requestError?.response?.data?.errorCode || requestError.errorCode);
      setError(requestError?.response?.data?.message || requestError.message || 'Unable to load this mock test.');
    } finally {
      setLoading(false);
    }
  }, [exam, location.pathname, location.state, navigate, parsed, scope]);

  useEffect(() => {
    if (!launchChecked) loadMetadata();
  }, [loadMetadata, launchChecked]);

  const launch = useCallback(async () => {
    if (!resolved?.access?.canStart && !resolved?.access?.canResume) return;
    setLaunching(true);
    setError(null);
    try {
      const access = await fetchMockTestAccess();
      if (!canAccessMockTest(access, exam === 'pte' ? 'pte' : `ielts_${scope}`, parsed.id)) {
        throw responseError({ errorCode: 'MOCK_TEST_NOT_ASSIGNED', message: 'This mock test is not assigned to you by your institution.' });
      }
      const launchData = exam === 'pte'
        ? await ResolvePteMockTest(scope, parsed.id, true)
        : await ResolveIeltsMockTest(scope, parsed.id, true);
      if (launchData?.responseCode !== 200 || !launchData?.response) throw responseError(launchData, 'Unable to start this mock test.');
      const context = launchData.response;
      storeLaunchGrant({ exam, scope, id: context.id }, context.launchGrant);

      if (exam === 'pte') {
        setLaunchChecked(true);
        navigate(location.pathname, {
          replace: true,
          state: { launched: true, resolvedTest: context, Exam: context.runnerData, launchGrant: context.launchGrant },
        });
        return;
      }

      if (scope === 'full') {
        const resumeAttemptId = location.state?.resumeFullTestAttemptId || location.state?.requestedResumeAttemptId || context.resumeAttemptId;
        if (resumeAttemptId && !location.state?.forceRestart) {
          const resumeResponse = await fetch(
            `${Base_URL}/app/users/ielts-tests/full-attempts/${resumeAttemptId}/resume-context`,
            { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } }
          );
          const resumeData = await resumeResponse.json();
          if (!resumeResponse.ok || resumeData?.responseCode !== 200) throw responseError(resumeData, 'Unable to resume this IELTS test.');
          setLaunchChecked(true);
          navigate(location.pathname, {
            replace: true,
            state: {
              launched: true,
              resolvedTest: context,
              groupedTests: context.runnerData.groupedTests,
              groupId: context.id,
              mode: location.state?.requestedMode || 'practice',
              continuePrevious: true,
              resumeFullTestAttemptId: resumeAttemptId,
              prefetchedResumeContext: resumeData.response,
              launchGrant: context.launchGrant,
            },
          });
        } else {
          setLaunchChecked(true);
          navigate(location.pathname, {
            replace: true,
            state: {
              launched: true,
              resolvedTest: context,
              groupedTests: context.runnerData.groupedTests,
              groupId: context.id,
              mode: location.state?.requestedMode || 'practice',
              continuePrevious: false,
              launchGrant: context.launchGrant,
            },
          });
        }
        return;
      }

      if (!pendingDraftKey.current) pendingDraftKey.current = `ielts:sectional:${String(context.category).toLowerCase()}:test-${context.id}:${uuid()}`;
      const attempt = await startIeltsSection({
        testId: context.id,
        section: context.category || context.runnerData?.category_name,
        draftId: location.state?.forceRestart ? undefined : location.state?.ieltsAttempt?.draftId || context.resumeDraftId,
        draftKey: pendingDraftKey.current || undefined,
        launchGrant: context.launchGrant,
        restoreLocal: !location.state?.forceRestart && Boolean(location.state?.resumeDraft || location.state?.requestedMode === 'resume'),
      });
      pendingDraftKey.current = attempt.draftKey;
      const detailResponse = await fetch(`${Base_URL}/app/users/ielts-tests/getTestById/${context.id}?draftId=${attempt.draftId}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Test-Launch-Token': context.launchGrant },
      });
      const detailData = await detailResponse.json();
      if (!detailResponse.ok || detailData?.responseCode !== 200 || !detailData?.response) {
        throw responseError(detailData, 'Unable to prepare this IELTS test.');
      }
      setLaunchChecked(true);
      navigate(location.pathname, {
        replace: true,
        state: {
          launched: true,
          resolvedTest: context,
          testData: detailData.response,
          ieltsAttempt: attempt,
          launchGrant: detailData.response.launchGrant || context.launchGrant,
          mode: location.state?.requestedMode === 'resume' ? undefined : location.state?.requestedMode,
          resumeDraft: Boolean(context.resumeDraftId || location.state?.resumeDraft || location.state?.requestedMode === 'resume'),
          currentUserId: JSON.parse(localStorage.getItem('userData') || '{}')?.UserId,
        },
      });
    } catch (launchError) {
      setErrorCode(launchError?.response?.data?.errorCode || launchError.errorCode);
      setError(launchError?.response?.data?.message || launchError.message || 'Unable to start this mock test.');
    } finally {
      setLaunching(false);
    }
  }, [exam, location.pathname, location.state, navigate, parsed, resolved, scope]);

  useEffect(() => {
    if (
      launchChecked
      || !mockAccess.access
      || error
      || !resolvedMatchesRoute
      || getSharedRouteAction(resolved) !== 'launch'
      || autoLaunchStarted.current
    ) return;

    autoLaunchStarted.current = true;
    launch();
  }, [error, launch, launchChecked, resolved, resolvedMatchesRoute, mockAccess.access]);

  if (launchChecked && location.state?.launched) {
    if (exam === 'pte') {
      return <PteAttemptContext.Provider value={location.state?.Exam?.ResumeTestId || null}>{scope === 'full' ? <MockTestFull /> : <MockTestSectional />}</PteAttemptContext.Provider>;
    }
    if (scope === 'full') return <IELTSFullTestPage />;
    const category = location.state?.testData?.category_name;
    const Runner = RunnerByCategory[category];
    return Runner ? <IeltsAttemptContext.Provider value={location.state?.ieltsAttempt}><Runner /></IeltsAttemptContext.Provider> : <Alert severity="error">This IELTS section is unavailable.</Alert>;
  }

  if (!mockAccess.access) return <Box sx={{ p: 3 }}><MockTestAccessStatus {...mockAccess} /></Box>;
  const assigned = canAccessMockTest(mockAccess.access, exam === 'pte' ? 'pte' : `ielts_${scope}`, parsed?.id);
  const notAssigned = !assigned || errorCode === 'MOCK_TEST_NOT_ASSIGNED';

  const routeAction = getSharedRouteAction(resolvedMatchesRoute ? resolved : null);
  if (!notAssigned && (loading || launching || (parsed && !resolvedMatchesRoute && !error) || (!error && routeAction === 'launch'))) {
    return <Box minHeight="60vh" display="grid" sx={{ placeItems: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', px: 2, py: 5 }}>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 2 }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h5" fontWeight={800}>{resolved?.name || 'Mock test'}</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              {[resolved?.variant, resolved?.category, resolved?.scope].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          {notAssigned && <Alert severity="warning">This mock test is not assigned to you by your institution.</Alert>}
          {!notAssigned && resolved?.access?.requiresSubscription && (
            <Alert severity="info" icon={<LockOutlinedIcon />}>A subscription is required to start this mock test.</Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            {error && <Button onClick={() => { autoLaunchStarted.current = false; loadMetadata(); mockAccess.refresh(); }}>Retry</Button>}
            {!notAssigned && resolved?.access?.requiresSubscription && routeAction === 'locked' && (
              <Button variant="contained" onClick={() => navigate('/plan-info', { state: { from: location.pathname } })}>
                Upgrade
              </Button>
            )}
            <Button variant="text" onClick={() => navigate(exam === 'pte' ? '/MockTest' : '/ielts-dashboard')}>Back to tests</Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
